import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Maximize2, RefreshCw, X, AlertCircle, ZoomIn, ZoomOut } from 'lucide-react';
import { getLatestImage } from '../../api/lastImage.api';
import { ultimaFotoIA } from '../weather/clima/fotosObra';
import { getRotationDegrees } from '../../../utils/cameraOrientation.js';
import useSyncedZoom, { ZoomIndicator } from '../firstLastImageCompare/useSyncedZoom';
import { momentoDeFoto, textoMomento, hace, REFRESCO_MS } from './fotoEnVivo.utils';

// Inicio › "Foto en vivo": la última foto que la cámara ya subió a S3 (la misma que usan Antes y Ahora y el
// Clima: webback /api/s3-images/:feed/latest; si no responde, la última que analizó la IA). Se actualiza sola
// cada minuto mientras la pestaña está a la vista. NUNCA le pide una foto a la cámara (las 4G no se despiertan).

const precargar = (url) => new Promise((ok, mal) => { const i = new Image(); i.onload = () => ok(url); i.onerror = mal; i.src = url; });

async function traerUltima(user) {
  try {
    const r = await getLatestImage(user.feedID);
    const url = typeof r === 'string' ? r : r?.url;
    if (url) return { url, ms: momentoDeFoto({ date: typeof r === 'object' ? r?.date : null, url }) };
    // respuesta vacía: la cámara todavía no subió nada (o no hay por webback): probar con la IA
  } catch (e) {
    if (e?.response?.status && e.response.status !== 404 && !user?.identifier) throw e;
  }
  const ia = await ultimaFotoIA(user).catch(() => null);
  return ia ? { url: ia.url, ms: ia.ms } : null;
}

// Estilo de la foto según cómo está montada la cámara (90/270 = vertical: se rota dentro de la caja 16:9).
const estiloRotada = (deg) => {
  if (deg === 90 || deg === 270) return { position: 'absolute', left: '50%', top: '50%', width: '56.25%', height: 'auto', transform: `translate(-50%, -50%) rotate(${deg}deg)` };
  if (deg === 180) return { transform: 'rotate(180deg)' };
  return undefined;
};

function VisorGrande({ foto, deg, onClose }) {
  const zoom = useSyncedZoom({ maxScale: 8 });
  const cerrar = useRef(null);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !(zoom.state.current.s > 1)) onClose(); };
    window.addEventListener('keydown', k);
    cerrar.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = prev; };
  }, [onClose, zoom.state]);
  const paso = (f) => {
    const z = zoom.state.current, el = zoom.viewportNode.current;
    if (!el) return;
    const s = Math.min(8, Math.max(1, z.s * f));
    const cx = el.clientWidth / 2, cy = el.clientHeight / 2;
    z.x = cx - ((cx - z.x) / z.s) * s;
    z.y = cy - ((cy - z.y) / z.s) * s;
    z.s = s;
    if (s === 1) { zoom.reset(true); return; }
    zoom.schedule();
  };
  const vertical = deg === 90 || deg === 270;
  return (
    <div className="fixed inset-0 z-[80] bg-black flex flex-col" role="dialog" aria-modal="true" aria-label="Foto en tamaño completo">
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-white">
        <p className="text-sm font-medium truncate">Última foto · {textoMomento(foto.ms) || 'sin fecha'}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => paso(1.5)} className="p-2 rounded-lg hover:bg-white/10" aria-label="Acercar"><ZoomIn className="w-5 h-5" /></button>
          <button type="button" onClick={() => paso(1 / 1.5)} className="p-2 rounded-lg hover:bg-white/10" aria-label="Alejar"><ZoomOut className="w-5 h-5" /></button>
          <a href={foto.url} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-white/10">Abrir original</a>
          <button ref={cerrar} type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label="Cerrar"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div ref={zoom.viewportRef} tabIndex={0} className="relative flex-1 overflow-hidden touch-none select-none focus:outline-none" style={{ cursor: 'zoom-in' }}>
        <div ref={zoom.stageRef} className="absolute inset-0 flex items-center justify-center" style={{ transformOrigin: '0 0' }}>
          <img src={foto.url} alt="Última foto de la obra" draggable={false} className={vertical ? 'object-contain' : 'max-w-full max-h-full object-contain'} style={vertical ? { transform: `rotate(${deg}deg)`, maxWidth: 'calc(100dvh - 96px)', maxHeight: '100vw' } : estiloRotada(deg)} />
        </div>
        <ZoomIndicator ref={zoom.indicatorRef} onReset={() => zoom.reset(true)} />
      </div>
      <p className="text-center text-[11px] text-white/60 py-1.5">Rueda del mouse o pellizcá para hacer zoom · arrastrá para moverte · Esc para cerrar</p>
    </div>
  );
}

const FotoEnVivo = ({ user }) => {
  const feedId = user?.feedID;
  const deg = getRotationDegrees(user?.orientation);
  const [st, setSt] = useState({ estado: 'cargando' }); // cargando | lista | vacia | error
  const [actualizando, setActualizando] = useState(false);
  const [grande, setGrande] = useState(false);
  const [ahora, setAhora] = useState(Date.now());
  const pedido = useRef(0);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!feedId) return;
    const id = ++pedido.current;
    if (!silencioso) setSt((s) => (s.estado === 'lista' ? s : { estado: 'cargando' }));
    setActualizando(true);
    try {
      const f = await traerUltima(user);
      if (id !== pedido.current) return;
      if (!f) { setSt({ estado: 'vacia' }); return; }
      await precargar(f.url).catch(() => null); // se cambia recién cuando la nueva ya está decodificada (sin parpadeo)
      if (id !== pedido.current) return;
      setSt((s) => (s.estado === 'lista' && s.foto.url === f.url ? s : { estado: 'lista', foto: f }));
    } catch (e) {
      if (id !== pedido.current) return;
      setSt((s) => (s.estado === 'lista' ? { ...s, avisoError: true } : { estado: 'error' }));
    } finally {
      if (id === pedido.current) { setActualizando(false); setAhora(Date.now()); }
    }
  }, [feedId, user?.identifier, user?.customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar(); return () => { pedido.current += 1; }; }, [cargar]);

  // Cada minuto, sólo con la pestaña del navegador a la vista; al volver a la pestaña, actualiza enseguida.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') cargar({ silencioso: true }); }, REFRESCO_MS);
    const vis = () => { if (document.visibilityState === 'visible') cargar({ silencioso: true }); };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, [cargar]);

  if (!feedId) return null;
  const foto = st.foto;
  const vieja = foto?.ms && ahora - foto.ms > 3 * 3600e3;

  return (
    <div className="space-y-2">
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-900">
        {st.estado === 'cargando' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-300" role="status">
            <Loader2 className="w-7 h-7 animate-spin" />
            <span className="text-sm">Buscando la última foto…</span>
          </div>
        )}
        {st.estado === 'vacia' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-gray-200">
            <Camera className="w-9 h-9 text-gray-400" />
            <p className="text-sm font-semibold">Todavía no llegó ninguna foto de esta cámara</p>
            <p className="text-xs text-gray-400">Apenas la cámara suba la primera, aparece acá sola.</p>
          </div>
        )}
        {st.estado === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-gray-200">
            <AlertCircle className="w-8 h-8 text-amber-400" />
            <p className="text-sm font-semibold">No pudimos traer la última foto</p>
            <button type="button" onClick={() => cargar()} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5"><RefreshCw className="w-4 h-4" />Probar de nuevo</button>
          </div>
        )}
        {foto && (
          <button type="button" onClick={() => setGrande(true)} className="group absolute inset-0 w-full h-full" aria-label="Ver la foto en tamaño completo">
            <img src={foto.url} alt="Última foto de la obra" className="w-full h-full object-cover" style={estiloRotada(deg)} />
            <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              <span className={`w-2 h-2 rounded-full ${vieja ? 'bg-amber-400' : 'bg-red-500 animate-pulse'}`} aria-hidden="true" />
              Última foto: {textoMomento(foto.ms) || 'sin fecha'}
            </span>
            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full opacity-90 group-hover:opacity-100">
              <Maximize2 className="w-3.5 h-3.5" />Ampliar
            </span>
          </button>
        )}
      </div>
      {foto && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-500">
          <span>
            {foto.ms ? <>Tomada {hace(foto.ms, ahora)}. </> : null}
            {vieja ? <b className="text-amber-700">La cámara no manda fotos nuevas hace un rato.</b> : 'Se actualiza sola cada minuto.'}
            {st.avisoError && <span className="text-amber-700"> No pudimos actualizarla, reintentamos en un minuto.</span>}
          </span>
          <button type="button" onClick={() => cargar({ silencioso: true })} disabled={actualizando} className="inline-flex items-center gap-1 font-semibold text-blue-700 hover:underline disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${actualizando ? 'animate-spin' : ''}`} />Actualizar
          </button>
        </div>
      )}
      {grande && foto && <VisorGrande foto={foto} deg={deg} onClose={() => setGrande(false)} />}
    </div>
  );
};

export default FotoEnVivo;
